import dayjs from "dayjs";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { client } from "@client";
import { ErrorComponent } from "@components/ErrorComponent";
import { ShareGoalModal } from "@components/Goals/ShareGoalModal";
import { AppNav } from "@components/Header/AppNav";
import { FutureMoneyLogo } from "@components/Header/FutureMoneyLogo";
import { Loader } from "@components/Loader";
import { NODE_COUNT } from "@constants";
import { GoalsContextProvider } from "@contexts";
import { BeneficiaryType, Flow, GoalType, Routes, SipFrequency, SipStatus } from "@ctypes";
import { SENT_SHARE_GOAL_INVITES } from "@graphql";
import {
  useErrorBanner,
  useGoalDetailsBreakdown,
  useGoalFlow,
  useGoalsContext,
  useModal,
  useNavigateToStep,
  useTheme,
  useUpcomingTransactions,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { Content, Eyebrow, GhostButton, Grain, Page } from "@routes/DashboardRoute/Home/styles";
import { home, homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import {
  Standing,
  annualDepositBreakdown,
  convertCentsToDollar,
  deriveGoalStanding,
  formatCurrency,
  goalRate,
  goalYearsRemaining,
  projectGoal,
  solveAnnualContribution,
} from "@utils";
import { PathChart } from "./PathChart";
import { PerformanceHoldings } from "./PerformanceHoldings";
import { SidecarBreakdown } from "./SidecarBreakdown";
import { GoalBreakdownRouteProps, GoalScenario } from "./types";

const Masthead = styled.div`
  padding: 32px ${home.pagePad} 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackPill = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  align-self: flex-start;
  padding: 0 14px 0 10px;
  border: 1px solid ${home.line12};
  border-radius: 999px;
  background: none;
  font-family: ${home.sans};
  font-size: 13px;
  color: ${home.ink62};
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }
`;

const MastheadRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
`;

const GoalName = styled.h1`
  margin: 0;
  font-family: ${home.serif};
  font-size: 44px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.6px;
  color: ${home.ink};

  @media (max-width: 720px) {
    font-size: 34px;
  }
`;

const StatusPill = styled.div<{ $standing: Standing }>`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 24px;
  padding: 0 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $standing }) =>
    $standing === Standing.Short ? "rgba(224,155,124,0.20)" : "rgba(143,191,176,0.18)"};
  color: ${({ $standing }) => ($standing === Standing.Short ? "#E7B79E" : "#9DCBBB")};

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: currentColor;
    display: block;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

const SectionPad = styled.div`
  padding: 0 ${home.pagePad};
`;

/* ---------- verdict band ---------- */

const Verdict = styled.div<{ $standing: Standing }>`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  display: flex;
  align-items: center;
  gap: 34px;
  padding: 26px 32px;
  color: #f2efe9;
  border: 1px solid
    ${({ $standing }) =>
      $standing === Standing.Short ? "rgba(224,155,124,0.38)" : "rgba(143,191,176,0.34)"};
  background: ${({ $standing }) =>
    $standing === Standing.Short
      ? `radial-gradient(700px 460px at 4% -20%, rgba(224,155,124,0.36), transparent 66%),
         radial-gradient(620px 420px at 92% 120%, rgba(196,104,84,0.30), transparent 66%),
         linear-gradient(150deg, #2A1E22 0%, #16121A 100%)`
      : `radial-gradient(700px 460px at 4% -20%, rgba(143,191,176,0.30), transparent 66%),
         radial-gradient(620px 420px at 92% 120%, rgba(96,140,150,0.26), transparent 66%),
         linear-gradient(150deg, #1B2A2B 0%, #12181C 100%)`};

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E");
    opacity: 0.19;
    mix-blend-mode: overlay;
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const VerdictIcon = styled.div<{ $standing: Standing }>`
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: rgba(14, 18, 26, 0.34);
  border: 1px solid
    ${({ $standing }) =>
      $standing === Standing.Short ? "rgba(224,155,124,0.44)" : "rgba(143,191,176,0.44)"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $standing }) => ($standing === Standing.Short ? "#E7B79E" : "#B7DCCE")};
`;

const VerdictCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 700px;

  .title {
    font-family: ${home.serif};
    font-size: 25px;
    line-height: 1.2;
    letter-spacing: -0.3px;
  }

  .body {
    font-size: 14px;
    line-height: 1.55;
    color: rgba(242, 239, 233, 0.74);
  }

  .body b {
    font-weight: 600;
    color: #f2efe9;
  }
`;

const VerdictActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
  width: 232px;
  position: relative;
  z-index: 1;

  @media (max-width: 1100px) {
    margin-left: 0;
    width: 100%;
  }
`;

const PrimaryAction = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: #f2efe9;
  color: #0e121a;
  font-family: ${home.sans};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(1.08);
  }
`;

const GhostAction = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(242, 239, 233, 0.24);
  border-radius: 999px;
  background: none;
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 500;
  color: #f2efe9;
  cursor: pointer;

  &:hover {
    background: rgba(242, 239, 233, 0.09);
  }
`;

/* ---------- deposit strip ---------- */

const DepositStrip = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  border-radius: 18px;
  border: 1px solid ${home.line10};
  background: ${home.cardBg};
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease;

  &:hover {
    border-color: ${home.line18};
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: ${home.fill07};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${home.ink72};
  }

  .label {
    font-size: 15px;
    font-weight: 500;
  }

  .next {
    font-size: 13px;
    color: ${home.ink52};
    margin-top: 3px;
  }

  .chevron {
    margin-left: auto;
    flex-shrink: 0;
    color: ${home.ink44};
  }

  @media (max-width: 720px) {
    flex-wrap: wrap;
  }
`;

/* ---------- accounts + contributors + footer ---------- */

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${home.ink44};
`;

const AccountsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 20px;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const AccountCard = styled.div`
  width: 300px;
  flex-shrink: 0;
  border-radius: 18px;
  border: 1px solid ${home.line10};
  background: ${home.cardBg};
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .name {
    font-family: ${home.serif};
    font-size: 19px;
    letter-spacing: -0.2px;
  }

  .balance {
    font-size: 25px;
    font-weight: 600;
    letter-spacing: -0.7px;
    line-height: 1;
  }

  .note {
    font-size: 12px;
    color: ${home.ink44};
  }

  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const EmptyAccountsStrip = styled.button`
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-radius: 18px;
  border: 1px dashed ${home.line14};
  background: ${home.fill04};
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill07};
  }

  .copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .title {
    font-size: 13px;
    font-weight: 500;
    color: ${home.ink62};
  }

  .list {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: ${home.ink44};
    flex-wrap: wrap;
  }

  .manage {
    font-size: 13px;
    color: ${home.copper};
    margin-left: auto;
    flex-shrink: 0;
  }
`;

const ContributorsBand = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid ${home.line14};
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 28px 36px;
  color: #f2efe9;
  background:
    radial-gradient(560px 380px at 8% -10%, rgba(233, 164, 186, 0.4), transparent 66%),
    radial-gradient(600px 420px at 90% 110%, rgba(224, 155, 124, 0.38), transparent 66%),
    radial-gradient(420px 340px at 52% 46%, rgba(126, 112, 168, 0.24), transparent 70%),
    linear-gradient(150deg, #2b2130 0%, #14121b 100%);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E");
    opacity: 0.19;
    mix-blend-mode: overlay;
  }

  .copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 640px;
  }

  .title {
    font-family: ${home.serif};
    font-size: 24px;
    line-height: 1.2;
    letter-spacing: -0.3px;
  }

  .body {
    font-size: 14px;
    line-height: 1.55;
    color: rgba(242, 239, 233, 0.68);
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const InviteButton = styled(PrimaryAction)`
  padding: 0 26px;
  margin-left: auto;
  flex-shrink: 0;
  gap: 9px;
  position: relative;
  z-index: 1;

  @media (max-width: 1100px) {
    margin-left: 0;
  }
`;

const Footer = styled.div`
  padding: 44px ${home.pagePad} 40px;

  > div {
    padding-top: 24px;
    border-top: 1px solid ${home.line08};
    display: flex;
    align-items: flex-start;
    gap: 40px;
  }
`;

const Disclaimer = styled.div`
  font-size: 12px;
  line-height: 1.65;
  color: ${home.ink34};
  max-width: 820px;
`;

const FooterMark = styled.div`
  opacity: 0.5;
  margin-left: auto;
  transform: scale(0.78);
  transform-origin: right top;

  svg {
    display: block;
  }
`;

const weeklyOf = (annual: number) => Math.round(annual / 52);

export const GoalBreakdownRoute = () => {
  const location = useLocation();

  const { goalId, beneficiaryId } = (location.state as GoalBreakdownRouteProps) ?? {};

  return (
    <GoalsContextProvider beneficiaryId={beneficiaryId} goalId={goalId}>
      <GoalBreakdownRouteComponent />
    </GoalsContextProvider>
  );
};

const GoalBreakdownRouteComponent = () => {
  const { goalType } = useGoalsContext();

  if (goalType === GoalType.TrumpAccountSidecar) {
    return <SidecarBreakdown />;
  }

  return <GoalDetailPage />;
};

const GoalDetailPage = () => {
  const { theme } = useTheme();

  const { user, loading: userLoading, error } = useUser();
  const { beneficiary, beneficiaryId, goalId, goal } = useGoalsContext();

  const { data: goalDetails, loading: goalDetailsLoading } = useGoalDetailsBreakdown({
    variables: {
      beneficiaryId,
      goalId: goalId!,
      nodeCount: NODE_COUNT,
    },
  });

  useEffect(() => {
    client.query({
      query: SENT_SHARE_GOAL_INVITES,
      variables: { goalId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = userLoading || goalDetailsLoading;

  const beneficiaryName =
    (beneficiary?.type === BeneficiaryType.Self
      ? user?.preferredName || user?.firstName
      : beneficiary?.firstName) ||
    i18n.t(Translations.HomeYou) ||
    "";

  return (
    <Page style={homeCssVars(theme.meta.dark)}>
      <Grain aria-hidden="true" />
      <Content>
        <AppNav />

        {loading ? (
          <Masthead>
            <Loader />
          </Masthead>
        ) : error || !goal ? (
          <Masthead>
            <ErrorComponent />
          </Masthead>
        ) : (
          <GoalDetailContent beneficiaryName={beneficiaryName} goalDetails={goalDetails} />
        )}
      </Content>
    </Page>
  );
};

const GoalDetailContent = ({
  beneficiaryName,
  goalDetails,
}: {
  beneficiaryName: string;
  goalDetails?: ReturnType<typeof useGoalDetailsBreakdown>["data"];
}) => {
  const navigate = useNavigateToStep();
  const { show } = useModal();
  const { showErrorBanner } = useErrorBanner();
  const { hasBankAccount } = useUser();
  const { resetGoalProps } = useGoalFlow();
  const { beneficiaryId, goalId, goal, goalName, goalType, sipId } = useGoalsContext();

  const { data: upcoming } = useUpcomingTransactions();

  const nextTransfer = upcoming?.find((transaction) => transaction?.goalId === goalId);

  // The deposit editor is its own flow – Edit goal changes name/target,
  // this changes the recurring deposit.
  const openDepositEditor = () => {
    if (!hasBankAccount) {
      navigate(Routes.BankLinking, { state: { flow: Flow.B2B } });
      return;
    }

    if (goalId && beneficiaryId) {
      resetGoalProps({
        flow: Flow.EditGoal,
        goalId,
        beneficiaryId,
        goalName,
        goalType,
        ...(sipId && { sipId }),
      });

      navigate(Routes.GoalDeposit);
      return;
    }

    showErrorBanner();
  };

  const standing = deriveGoalStanding(goal!);
  const { status, current, target, projectedAtTarget, targetYear } = standing;

  const { activeAnnual, pausedAnnual } = annualDepositBreakdown(goal);
  const rate = goalRate(goal);
  const years = Math.max(goalYearsRemaining(goal), 1);

  const isPaused = activeAnnual === 0 && pausedAnnual > 0;
  const hasNoDeposit = activeAnnual === 0 && pausedAnnual === 0;

  const stoppedProjection = projectGoal(goal!, 0);
  const oldProjection = projectGoal(goal!, pausedAnnual);

  const neededAnnual = solveAnnualContribution(goal!) ?? 0;
  const recommendedWeekly = Math.max(Math.ceil(neededAnnual / 52), 1);
  const recommendedProjection = projectGoal(goal!, recommendedWeekly * 52);

  const currentWeekly = weeklyOf(activeAnnual);
  const oldWeekly = weeklyOf(pausedAnnual);

  const depositLabel = (() => {
    if (!sipId || !hasBankAccount) {
      return i18n.t(Translations.GoalBreakdownRouteCreateRecurringDeposit);
    }

    if (goal?.sips?.some((sip) => sip?.status === SipStatus.Paused)) {
      return i18n.t(Translations.GoalBreakdownRouteUnpauseRecurringDeposit);
    }

    if (goal?.sips?.some((sip) => sip?.recurringDepositSchedule === SipFrequency.Annually)) {
      return i18n.t(Translations.GoalDepositAnuallyLabel, {
        amount: formatCurrency(activeAnnual),
      });
    }

    if (goal?.sips?.some((sip) => sip?.recurringDepositSchedule === SipFrequency.Monthly)) {
      return i18n.t(Translations.GoalDepositMonthlyLabel, {
        amount: formatCurrency(activeAnnual / 12),
      });
    }

    if (goal?.sips?.some((sip) => sip?.recurringDepositSchedule === SipFrequency.Daily)) {
      return i18n.t(Translations.GoalDepositDailyLabel, {
        amount: formatCurrency(activeAnnual / 365),
      });
    }

    return i18n.t(Translations.GoalDepositWeeklyLabel, {
      amount: formatCurrency(currentWeekly),
    });
  })();

  const pauseCost = Math.round((activeAnnual * Math.pow(1 + rate, years - 1)) / 100) * 100;

  const giftGrowth = Math.max(Math.round((100 * Math.pow(1 + rate, years)) / 100) * 100, 100);

  const editGoal = () => navigate(Routes.EditGoal, { state: { beneficiaryId, goalId } });

  /* ---------- verdict ---------- */

  const verdict = (() => {
    if (status === Standing.Met) {
      return {
        title: i18n.t(Translations.GoalBreakdownRouteVerdictMetTitle),
        body: i18n.t(Translations.GoalBreakdownRouteVerdictMetBody, {
          current: formatCurrency(current, true),
          overage: formatCurrency(current - target),
          target: formatCurrency(target),
        }),
        primary: null as { label: string; onClick: () => void } | null,
        ghosts: [
          {
            label: i18n.t(Translations.GoalBreakdownRouteRaiseGoal),
            onClick: editGoal,
          },
        ],
      };
    }

    if (status === Standing.OnTrack) {
      return {
        title: i18n.t(Translations.GoalBreakdownRouteVerdictOnTitle),
        body: i18n.t(Translations.GoalBreakdownRouteVerdictOnBody, {
          weekly: formatCurrency(currentWeekly),
          projected: formatCurrency(projectedAtTarget),
          year: targetYear,
          surplus: formatCurrency(projectedAtTarget - target),
          target: formatCurrency(target),
          pauseCost: formatCurrency(pauseCost),
        }),
        primary: null,
        ghosts: [
          {
            label: i18n.t(Translations.GoalBreakdownRouteChangeDeposit),
            onClick: openDepositEditor,
          },
          {
            label: i18n.t(Translations.GoalBreakdownRouteAddOneOff),
            onClick: () => navigate(Routes.OneTimeContribution),
          },
        ],
      };
    }

    // Short.
    const bodyKey = isPaused
      ? Translations.GoalBreakdownRouteVerdictPausedBody
      : hasNoDeposit
        ? Translations.GoalBreakdownRouteVerdictNoDepositBody
        : Translations.GoalBreakdownRouteVerdictShortBody;

    return {
      title: isPaused
        ? i18n.t(Translations.GoalBreakdownRouteVerdictPausedTitle)
        : i18n.t(Translations.GoalBreakdownRouteVerdictShortTitle),
      body: i18n.t(bodyKey, {
        projected: formatCurrency(isPaused || hasNoDeposit ? stoppedProjection : projectedAtTarget),
        year: targetYear,
        target: formatCurrency(target),
        old: formatCurrency(oldWeekly),
        oldShort: formatCurrency(Math.max(target - oldProjection, 0)),
        weekly: formatCurrency(currentWeekly),
        gap: formatCurrency(Math.max(target - projectedAtTarget, 0)),
        recommended: formatCurrency(recommendedWeekly),
      }),
      primary: {
        label: i18n.t(Translations.GoalBreakdownRouteResumeAt, {
          amount: formatCurrency(recommendedWeekly),
        }),
        onClick: openDepositEditor,
      },
      ghosts: [
        {
          label: i18n.t(Translations.GoalBreakdownRoutePickAnother),
          onClick: openDepositEditor,
        },
      ],
    };
  })();

  /* ---------- path scenarios ---------- */

  const scenarios: Array<GoalScenario> = (() => {
    if (status === Standing.Short) {
      const list: Array<GoalScenario> = [
        {
          key: "paused",
          kind: "flat",
          label: i18n.t(
            isPaused || hasNoDeposit
              ? Translations.GoalBreakdownRouteIfStaysPaused
              : Translations.GoalBreakdownRouteIfStopped,
          ),
          annualDeposit: 0,
          projected: stoppedProjection,
          note: i18n.t(Translations.GoalBreakdownRouteShortBy, {
            amount: formatCurrency(Math.max(target - stoppedProjection, 0)),
          }),
        },
      ];

      if (isPaused && oldWeekly > 0) {
        list.push({
          key: "old",
          kind: "dashed",
          label: i18n.t(Translations.GoalBreakdownRouteRestartOld, {
            amount: formatCurrency(oldWeekly),
          }),
          annualDeposit: pausedAnnual,
          projected: oldProjection,
          note:
            oldProjection >= target
              ? i18n.t(Translations.GoalBreakdownRouteClearsBy, {
                  amount: formatCurrency(oldProjection - target),
                })
              : i18n.t(Translations.GoalBreakdownRouteShortBy, {
                  amount: formatCurrency(target - oldProjection),
                }),
        });
      }

      if (!isPaused && !hasNoDeposit) {
        list.push({
          key: "current",
          kind: "dashed",
          label: i18n.t(Translations.GoalBreakdownRouteCurrentDeposit, {
            amount: formatCurrency(currentWeekly),
          }),
          annualDeposit: activeAnnual,
          projected: projectedAtTarget,
          note: i18n.t(Translations.GoalBreakdownRouteShortBy, {
            amount: formatCurrency(Math.max(target - projectedAtTarget, 0)),
          }),
        });
      }

      list.push({
        key: "recommended",
        kind: "accent",
        label: i18n.t(Translations.GoalBreakdownRoutePerWeek, {
          amount: formatCurrency(recommendedWeekly),
        }),
        annualDeposit: recommendedWeekly * 52,
        projected: recommendedProjection,
        note: i18n.t(Translations.GoalBreakdownRouteClearsBy, {
          amount: formatCurrency(Math.max(recommendedProjection - target, 0)),
        }),
      });

      return list;
    }

    const list: Array<GoalScenario> = [
      {
        key: "current",
        kind: "accent",
        label: i18n.t(Translations.GoalBreakdownRouteCurrentDeposit, {
          amount: formatCurrency(currentWeekly),
        }),
        annualDeposit: activeAnnual,
        projected: projectedAtTarget,
        note:
          projectedAtTarget >= target
            ? i18n.t(Translations.GoalBreakdownRouteClearsBy, {
                amount: formatCurrency(projectedAtTarget - target),
              })
            : i18n.t(Translations.GoalBreakdownRouteShortBy, {
                amount: formatCurrency(target - projectedAtTarget),
              }),
      },
    ];

    if (activeAnnual > 0) {
      list.push({
        key: "stopped",
        kind: "dashed",
        label: i18n.t(Translations.GoalBreakdownRouteIfStopped),
        annualDeposit: 0,
        projected: stoppedProjection,
        note: i18n.t(Translations.GoalBreakdownRouteStoppedNote, {
          amount: formatCurrency(Math.max(target - stoppedProjection, 0)),
        }),
      });
    }

    return list;
  })();

  const invested = current + activeAnnual * years;
  const investedPct =
    projectedAtTarget > 0 ? Math.min(Math.max((invested / projectedAtTarget) * 100, 2), 98) : 50;

  const splitTile =
    status !== Standing.Short
      ? {
          title: i18n.t(Translations.GoalBreakdownRouteOfThat, {
            amount: formatCurrency(projectedAtTarget),
          }),
          investedPct,
          investedLabel: i18n.t(Translations.GoalBreakdownRouteYouAdd, {
            amount: formatCurrency(invested),
          }),
          growthLabel: i18n.t(Translations.GoalBreakdownRouteTimeAdds, {
            amount: formatCurrency(Math.max(projectedAtTarget - invested, 0)),
          }),
        }
      : undefined;

  /* ---------- accounts ---------- */

  const accounts = (goal?.accounts ?? []).filter(
    (account): account is NonNullable<typeof account> => Boolean(account),
  );
  const funded = accounts.filter((a) => (a.balanceInCents ?? 0) > 0);
  const empty = accounts.filter((a) => (a.balanceInCents ?? 0) === 0);

  return (
    <>
      <Masthead>
        <BackPill onClick={() => navigate(Routes.GoalsOverview, { state: { beneficiaryId } })}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 6-6 6 6 6" />
          </svg>
          {beneficiaryName}
        </BackPill>

        <MastheadRow>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Eyebrow>
                {i18n.t(Translations.GoalBreakdownRouteGoalFor, {
                  name: beneficiaryName,
                })}
              </Eyebrow>
              <StatusPill $standing={status}>
                <span className="dot" />
                {status === Standing.Met
                  ? i18n.t(Translations.GoalsOverviewRouteStatusMet)
                  : status === Standing.OnTrack
                    ? i18n.t(Translations.GoalsOverviewRouteStatusOnTrack)
                    : i18n.t(Translations.GoalBreakdownRouteOffTrack)}
              </StatusPill>
            </div>
            <GoalName>{goal?.name}</GoalName>
          </div>

          <Actions>
            <GhostButton onClick={() => navigate(Routes.EditGoal, { state: { beneficiaryId, goalId } })}>
              {i18n.t(Translations.GoalBreakdownRouteEditGoal)}
            </GhostButton>
            <GhostButton onClick={() => navigate(Routes.Withdraw, { state: { goalId } })}>
              {i18n.t(Translations.GoalBreakdownRouteWithdraw)}
            </GhostButton>
            <GhostButton onClick={() => show(<ShareGoalModal goalId={goalId!} />)}>
              {i18n.t(Translations.GoalBreakdownRouteShareGoal)}
            </GhostButton>
          </Actions>
        </MastheadRow>
      </Masthead>

      <SectionPad style={{ paddingBottom: 24 }}>
        <Verdict $standing={status}>
          <VerdictIcon $standing={status}>
            {status === Standing.Short && verdict.primary ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m5 13 4 4L19 7" />
              </svg>
            )}
          </VerdictIcon>

          <VerdictCopy>
            <div className="title">{verdict.title}</div>
            <div className="body">{verdict.body}</div>
          </VerdictCopy>

          <VerdictActions>
            {verdict.primary && (
              <PrimaryAction onClick={verdict.primary.onClick}>
                {verdict.primary.label}
              </PrimaryAction>
            )}
            {verdict.ghosts.map((ghost) => (
              <GhostAction key={ghost.label} onClick={ghost.onClick}>
                {ghost.label}
              </GhostAction>
            ))}
          </VerdictActions>
        </Verdict>
      </SectionPad>

      <SectionPad style={{ paddingBottom: 24 }}>
        <DepositStrip onClick={openDepositEditor}>
          <span className="icon">
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
          </span>
          <span>
            <div className="label">{depositLabel}</div>
            {nextTransfer?.date && (
              <div className="next">
                {i18n.t(Translations.HomeNextTransfer)} ·{" "}
                {dayjs(nextTransfer.date).format("MMM D, YYYY")} ·{" "}
                {formatCurrency(convertCentsToDollar(nextTransfer.amountInCents), true)}
              </div>
            )}
          </span>
          <span className="chevron">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 6 6 6-6 6" />
            </svg>
          </span>
        </DepositStrip>
      </SectionPad>

      <SectionPad>
        <PathChart
          goal={goal!}
          scenarios={scenarios}
          target={target}
          current={current}
          targetYear={targetYear}
          eyebrow={
            status === Standing.Short
              ? i18n.t(Translations.GoalBreakdownRouteWaysThisEnds, {
                  count: scenarios.length,
                  year: targetYear,
                })
              : i18n.t(Translations.GoalBreakdownRoutePathToTarget, {
                  year: targetYear,
                })
          }
          note={
            status === Standing.Short
              ? i18n.t(Translations.GoalBreakdownRouteOnlyOneClears)
              : i18n.t(Translations.GoalBreakdownRouteDepositClears)
          }
          splitTile={splitTile}
        />
      </SectionPad>

      <SectionPad style={{ paddingTop: 44 }}>
        <PerformanceHoldings goalDetails={goalDetails} startDate={goal?.startDate} />
      </SectionPad>

      {accounts.length > 0 && (
        <SectionPad
          style={{
            paddingTop: 36,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          <SectionLabel>{i18n.t(Translations.GoalBreakdownRouteAccountsFunding)}</SectionLabel>
          <AccountsRow>
            {funded.map((account) => (
              <AccountCard key={account.id}>
                <div className="name">
                  {account.title ?? i18n.t(Translations.GoalBreakdownRouteAccount)}
                </div>
                <div className="balance">
                  {formatCurrency(convertCentsToDollar(account.balanceInCents), true)}
                </div>
                <div className="note">{i18n.t(Translations.GoalBreakdownRouteOfGoalBalance)}</div>
              </AccountCard>
            ))}

            {empty.length > 0 && (
              <EmptyAccountsStrip onClick={() => navigate(Routes.Accounts)}>
                <span className="copy">
                  <span className="title">
                    {i18n.t(Translations.GoalBreakdownRouteEmptyAccounts, {
                      count: empty.length,
                    })}
                  </span>
                  <span className="list">
                    {empty.map((account) => (
                      <span key={account.id}>
                        {account.title ?? i18n.t(Translations.GoalBreakdownRouteAccount)} · $0.00
                      </span>
                    ))}
                  </span>
                </span>
                <span className="manage">
                  {i18n.t(Translations.GoalBreakdownRouteManageAccounts)} &rarr;
                </span>
              </EmptyAccountsStrip>
            )}
          </AccountsRow>
        </SectionPad>
      )}

      <SectionPad style={{ paddingTop: 36 }}>
        <ContributorsBand>
          <div className="copy">
            <div className="title">
              {status === Standing.Short
                ? i18n.t(Translations.GoalBreakdownRouteContribShortTitle, {
                    gap: formatCurrency(Math.max(target - projectedAtTarget, 0)),
                  })
                : i18n.t(Translations.GoalBreakdownRouteContribTitle, {
                    grown: formatCurrency(giftGrowth),
                    year: targetYear,
                  })}
            </div>
            <div className="body">{i18n.t(Translations.GoalBreakdownRouteContribBody)}</div>
          </div>
          <InviteButton onClick={() => show(<ShareGoalModal goalId={goalId!} />)}>
            {i18n.t(Translations.GoalBreakdownRouteInvite)}
          </InviteButton>
        </ContributorsBand>
      </SectionPad>

      <Footer>
        <div>
          <Disclaimer>
            {i18n.t(Translations.GoalBreakdownRouteDisclosure, {
              rate: Math.round(goalRate(goal) * 100),
            })}
          </Disclaimer>
          <FooterMark>
            <FutureMoneyLogo />
          </FooterMark>
        </div>
      </Footer>
    </>
  );
};
